import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import classes from "./KeikkaIcon2.module.css";
import { Fade } from "@mui/material";
import { useDarkModeData } from "src/context/DarkModeContext";
import keikkaClass from "src/data/keikka/keikkaClass";

const MAX_M3 = 50;
const MAX_LINJA = 85;
const SET_TIMEOUT_DELAY = 1000;

export default function KeikkaIcon2({ gridKeikka, ...props }) {
  const { darkMode } = useDarkModeData();
  const path = "/img/keikkaIcons2/";
  const keikkaData = props.keikkaData || new keikkaClass();
  const printIcon = props.printIcon || false;
  const size = props.size || 40;
  const opacity = props.opacity === undefined ? (printIcon ? 0.1 : 0.85) : props.opacity;
  const mirror = props.mirror || false;
  const noAnimation = props.noAnimation || false;
  const [fileNames, setFileNames] = useState([]);
  const [fills, setFills] = useState([]);

  const get_isPumi = useMemo(() => {
    let isPumi = false;
    if (props.keikkaData) isPumi = props.keikkaData.pumppu.isPumi;
    else if (gridKeikka) isPumi = gridKeikka.vehicleTypeId === 2 || gridKeikka.vehicleTypeId === 1;
    return isPumi;
  }, [keikkaData?.pumppu.isPumi, gridKeikka?.vehicleTypeId]);

  const get_isPumppu = useMemo(() => {
    if (props.keikkaData) return props.keikkaData.pumppu.isPumppu;
    if (gridKeikka) return gridKeikka.vehicleTypeId === 3;
    return false;
  }, [keikkaData?.pumppu.isPumppu, gridKeikka?.vehicleTypeId]);

  const get_PumppuLinja = useMemo(() => {
    if (props.keikkaData) return props.keikkaData.pumppu.pumppuLinja;
    if (gridKeikka) return gridKeikka.pumppuLinja;
    return null;
  }, [keikkaData?.pumppu.pumppuLinja, gridKeikka?.pumppuLinja]);

  const get_PumppuPuomi = useMemo(() => {
    if (props.keikkaData) return props.keikkaData.pumppu.pumppuPuomi;
    if (gridKeikka) return gridKeikka.pumppuPuomi;
    return null;
  }, [keikkaData?.pumppu.pumppuPuomi, gridKeikka?.pumppuPuomi]);

  const get_TotM3 = useMemo(() => {
    if (props.keikkaData) {
      if (props.keikkaData.modules.hasBetoni) return props.keikkaData.totM3;
      return 0;
    }
    if (gridKeikka) return gridKeikka.m3;
    return null;
  }, [keikkaData?.totM3, gridKeikka?.m3]);

  const inversePercent = darkMode === true ? "80%" : "20%";

  const styles = useMemo(
    () => ({
      div: {
        opacity: opacity,
        filter: `invert(${inversePercent})`,
        width: size,
        height: size,
        transform: `scaleX(${mirror ? -1 : 1})`, // Mirror image
      },
    }),
    [opacity, inversePercent, size, mirror]
  );

  useEffect(() => {
    const f = getFileNames;
    if (noAnimation) {
      setFileNames(f.files);
      setFills(f.fills);
    } else {
      setTimeout(() => {
        setFileNames(f.files);
        setFills(f.fills);
      }, SET_TIMEOUT_DELAY);
    }
  }, [
    keikkaData?.pumppu.pumppuPuomi,
    keikkaData?.pumppu.pumppuLinja,
    keikkaData.totM3,
    keikkaData.modules?.hasBetoni,
    keikkaData.modules?.hasPumppu,
    gridKeikka?.pumppuPuomi,
    gridKeikka?.pumppuLinja,
    gridKeikka?.vehicleTypeId,
  ]);

  const getFileNames = useMemo(() => {
    const totm3 = get_TotM3; // keikkaData.modules.hasBetoni ? keikkaData.totM3 : 0;
    const maxM3 = MAX_M3;
    const maxLinja = MAX_LINJA;
    let f = [];
    let fills = [];
    if (gridKeikka || keikkaData.modules.hasPumppu) {
      if (get_isPumi) {
        if (get_PumppuPuomi > 0) {
          // pumi with boom
          f.push("PB.png");
          f.push("BS/B1.png");
          const _a = Math.min(Math.round((totm3 * 10) / maxM3), 10);
          if (_a > 0) f.push("PBF/PBF" + _a + ".png");
        } else {
          // pumi no boom
          f.push("PNB.png");
          //f.push("BS/S0.png");
          const _a = Math.round((totm3 * 10) / maxM3);
          if (_a > 0) fills.push("PBF/PBF" + _a + ".png");
        }
      } else if (get_isPumppu) {
        if (totm3 > 40) {
          if (get_PumppuPuomi > 0) {
            // big pump boom
            f.push("BPB.png");
            f.push("BS/B1.png");
          } else {
            // big pump no boom
            f.push("BPNB.png");
            //f.push("BS/S0.png");
          }
        } else {
          if (get_PumppuPuomi > 0) {
            // small pump boom
            f.push("SPB.png");
            f.push("BS/B1.png");
          } else {
            // small pump no boom
            f.push("SPNB.png");
          }
        }
      }
      // Get linjajutut
      if (get_PumppuPuomi > 0) {
        const maxNum = Math.min((get_PumppuLinja * 12) / maxLinja + 1, 13);
        // get linjafiles for boom (B2-B13)
        for (let i = 2; i <= maxNum; i++) {
          if (i == 1) f.push("BS/S0.png");
          f.push("BS/B" + i + ".png");
        }
      } else {
        const maxNum = (get_PumppuLinja * 11) / maxLinja;
        // get linjafiles for noboom (S1-S12)
        for (let i = 1; i <= maxNum; i++) {
          if (i == 1) f.push("BS/S0.png");
          let s = "BS/S" + i + ".png";
          switch (s) {
            case "BS/S2.png":
              s = "BS/B5.png";
              break;
            case "BS/S3.png":
              s = "BS/B4.png";
              break;
            case "BS/S4.png":
              s = "BS/B3.png";
              break;
            case "BS/S8.png":
              s = "BS/B12.png";
              break;
            case "BS/S9.png":
              s = "BS/B11.png";
              break;
            case "BS/S10.png":
              s = "BS/B10.png";
              break;
            case "BS/S11.png":
              s = "BS/B9.png";
              break;
            case "BS/S12.png":
              s = "BS/B8.png";
              break;
            default:
              s = "BS/S" + i + ".png";
              break;
          }
          f.push(s);
        }
      }
    } else {
      console.log("No keikkaicon as keikkaData.modules.hasPumppu === false");
    }
    return { files: f, fills: fills };
  }, [
    get_TotM3,
    get_isPumi,
    get_isPumppu,
    get_PumppuLinja,
    get_PumppuPuomi,
    gridKeikka,
    keikkaData.modules,
    gridKeikka?.vehicleTypeId,
  ]);

  const transitionDelay1 = noAnimation ? 0 : Math.floor(Math.random() * 500);
  const transitionDelay2 = noAnimation ? 0 : 300 + Math.floor(Math.random() * 400);
  const timeout = noAnimation ? 0 : Math.floor(Math.random() * 800) + 200;

  return (
    <div className={classes.div} style={styles.div}>
      {fileNames.map((f) => (
        <div key={f}>
          <Fade style={{ transitionDelay: `${transitionDelay1}ms` }} in={true} timeout={timeout}>
            <img src={`${path}${f}`} className={classes.icon} />
          </Fade>
        </div>
      ))}
      {fills.map((f) => (
        <div key={f} className={classes.fillsDiv}>
          <Fade style={{ transitionDelay: `${transitionDelay2}ms` }} in={true} timeout={timeout}>
            <img src={`${path}${f}`} className={classes.icon} />
          </Fade>
        </div>
      ))}
    </div>
  );
}

KeikkaIcon2.propTypes = {
  keikkaData: PropTypes.object,
  printIcon: PropTypes.bool,
  mirror: PropTypes.bool,
  size: PropTypes.number,
  noAnimation: PropTypes.bool,
  opacity: PropTypes.number,
  inversePercent: PropTypes.string,
  gridKeikka: PropTypes.object,
};
